<template>
    <AppLayout>
        <div class="container p-4 mx-auto md:px-1 md:py-4">
            <!-- breadcrumb -->
            <nav
                class="mb-6 text-sm font-semibold"
                aria-label="Breadcrumb"
            >
                <ol class="inline-flex p-0 list-none">
                    <li class="flex items-center text-blue-500">
                        <inertia-link
                            :href="route('landing')"
                            class="text-gray-600"
                        >
                            Home
                        </inertia-link>
                        <svg
                            fill="#34D399"
                            class="w-3 h-3 mx-3"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                        >
                            <path
                                d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                            />
                        </svg>
                    </li>
                    <li class="flex items-center">
                        <a
                            href="#"
                            class="text-gray-600"
                            >Invoice</a
                        >
                    </li>
                </ol>
            </nav>
            <!-- breadcrumb end -->

            <div class="flex flex-col justify-between space-y-2 lg:flex-row lg:space-y-0 lg:items-center">
                <div class="text-xl font-semibold md:text-2xl">My Invoices</div>
                <div>
                    <t-input
                        id="retailer-name-search-box"
                        v-model="search"
                        placeholder="Search by name"
                    />
                </div>
            </div>

            <div
                v-if="bills.data.length"
                class="my-6"
            >
                <t-table
                    :responsive="true"
                    :responsive-breakpoint="640"
                    :headers="currentTableHeaders"
                    :data="groupedBills()"
                >
                    <template
                        slot="tbody"
                        slot-scope="props"
                    >
                        <tbody :class="props.tbodyClass">
                            <!-- today title -->
                            <tr
                                v-if="props.data[0].length"
                                :class="props.trClass"
                                class="font-bold"
                            >
                                <td
                                    :class="props.tdClass"
                                    :colspan="currentTableHeaders.length"
                                >
                                    <p>Today</p>
                                </td>
                            </tr>
                            <!-- today's bills -->
                            <tr
                                v-for="bill in props.data[0]"
                                :key="'bill-' + bill.id"
                                :class="[props.trClass]"
                                class="cursor-pointer"
                                @click="goto('retailer.invoice.print', { billId: bill.id })"
                            >
                                <!-- wholesaler name -->
                                <td
                                    v-if="!isWholesaler"
                                    :class="props.tdClass"
                                >
                                    {{ bill.wholesaler.name }}
                                </td>
                                <!-- Retailer Name -->
                                <td :class="props.tdClass">
                                    {{ bill.user.name }}
                                </td>
                                <!-- transaction count -->
                                <td :class="props.tdClass">
                                    {{ bill.transactions.length }}
                                </td>
                                <!-- total items in transaction -->
                                <td :class="props.tdClass">
                                    {{ getTotalQuantity(bill.transactions) }}
                                </td>
                                <!-- total transaction amount -->
                                <td :class="[props.tdClass, 'text-green-500']">
                                    {{ config(common.currency) }} {{ sumAmountFromTransactions(bill.transactions) }}
                                </td>
                                <!-- Payment Status  -->
                                <td
                                    :class="
                                        hasPendingUserPayments(bill)
                                            ? 'bg-red-400'
                                            : getPaymentStatus(bill) === 'Paid'
                                            ? 'bg-green-400'
                                            : 'bg-yellow-400'
                                    "
                                    class="inline-block py-1 mt-1 text-sm text-white rounded"
                                >
                                    {{ hasPendingUserPayments(bill) ? 'Declared' : getPaymentStatus(bill) }}
                                </td>
                                <!-- Date  -->
                                <td>
                                    {{ bill.created_at | fromNow }}
                                </td>
                            </tr>

                            <!-- yesterday title -->
                            <tr
                                v-if="props.data[1].length"
                                :class="props.trClass"
                                class="font-bold"
                            >
                                <td
                                    :class="props.tdClass"
                                    :colspan="currentTableHeaders.length"
                                >
                                    Yesterday
                                </td>
                            </tr>
                            <!-- yesterday's bills -->
                            <tr
                                v-for="bill in props.data[1]"
                                :key="'bill-' + bill.id"
                                :class="[props.trClass]"
                                class="cursor-pointer"
                                @click="goto('retailer.invoice.print', { billId: bill.id })"
                            >
                                <!-- wholesaler name -->
                                <td
                                    v-if="!isWholesaler"
                                    :class="props.tdClass"
                                >
                                    {{ bill.wholesaler.name }}
                                </td>
                                <!-- Retailer Name -->
                                <td :class="props.tdClass">
                                    {{ bill.user.name }}
                                </td>
                                <!-- transaction count -->
                                <td :class="props.tdClass">
                                    {{ bill.transactions.length }}
                                </td>
                                <!-- total items in transaction -->
                                <td :class="props.tdClass">
                                    {{ getTotalQuantity(bill.transactions) }}
                                </td>
                                <!-- total transaction amount -->
                                <td :class="[props.tdClass, 'text-green-500']">
                                    {{ config(common.currency) }} {{ sumAmountFromTransactions(bill.transactions) }}
                                </td>
                                <!-- Payment Status  -->
                                <td
                                    :class="getPaymentStatus(bill) === 'Paid' ? 'bg-green-400' : 'bg-yellow-400'"
                                    class="inline-block px-2 py-1 mt-1 text-sm text-white rounded"
                                >
                                    {{ hasPendingUserPayments(bill) ? 'Declared' : getPaymentStatus(bill) }}
                                </td>
                                <!-- Date  -->
                                <td>
                                    {{ bill.created_at | fromNow }}
                                </td>
                            </tr>

                            <!-- this week title -->
                            <tr
                                v-if="props.data[2].length"
                                :class="props.trClass"
                                class="font-bold"
                            >
                                <td
                                    :class="props.tdClass"
                                    :colspan="currentTableHeaders.length"
                                >
                                    This Week
                                </td>
                            </tr>
                            <!-- this week's bills -->
                            <tr
                                v-for="bill in props.data[2]"
                                :key="'bill-' + bill.id"
                                :class="[props.trClass]"
                                class="cursor-pointer"
                                @click="goto('retailer.invoice.print', { billId: bill.id })"
                            >
                                <!-- wholesaler name -->
                                <td
                                    v-if="!isWholesaler"
                                    :class="props.tdClass"
                                >
                                    {{ bill.wholesaler.name }}
                                </td>
                                <!-- Retailer Name -->
                                <td :class="props.tdClass">
                                    {{ bill.user.name }}
                                </td>
                                <!-- transaction count -->
                                <td :class="props.tdClass">
                                    {{ bill.transactions.length }}
                                </td>
                                <!-- total items in transaction -->
                                <td :class="props.tdClass">
                                    {{ getTotalQuantity(bill.transactions) }}
                                </td>
                                <!-- total transaction amount -->
                                <td :class="[props.tdClass, 'text-green-500']">
                                    {{ config(common.currency) }} {{ sumAmountFromTransactions(bill.transactions) }}
                                </td>
                                <!-- Payment Status  -->
                                <td
                                    :class="getPaymentStatus(bill) === 'Paid' ? 'bg-green-400' : 'bg-yellow-400'"
                                    class="inline-block px-2 py-1 mt-1 text-sm text-white rounded"
                                >
                                    {{ hasPendingUserPayments(bill) ? 'Declared' : getPaymentStatus(bill) }}
                                </td>
                                <!-- Date  -->
                                <td>
                                    {{ bill.created_at | fromNow }}
                                </td>
                            </tr>

                            <!-- last week title -->
                            <tr
                                v-if="props.data[3].length"
                                :class="props.trClass"
                                class="font-bold"
                            >
                                <td
                                    :class="props.tdClass"
                                    :colspan="currentTableHeaders.length"
                                >
                                    Last Week
                                </td>
                            </tr>
                            <!-- last week's bills -->
                            <tr
                                v-for="bill in props.data[3]"
                                :key="'bill-' + bill.id"
                                :class="[props.trClass]"
                                class="cursor-pointer"
                                @click="goto('retailer.invoice.print', { billId: bill.id })"
                            >
                                <!-- wholesaler name -->
                                <td
                                    v-if="!isWholesaler"
                                    :class="props.tdClass"
                                >
                                    {{ bill.wholesaler.name }}
                                </td>
                                <!-- Retailer Name -->
                                <td :class="props.tdClass">
                                    {{ bill.user.name }}
                                </td>
                                <!-- transaction count -->
                                <td :class="props.tdClass">
                                    {{ bill.transactions.length }}
                                </td>
                                <!-- total items in transaction -->
                                <td :class="props.tdClass">
                                    {{ getTotalQuantity(bill.transactions) }}
                                </td>
                                <!-- total transaction amount -->
                                <td :class="[props.tdClass, 'text-green-500']">
                                    {{ config(common.currency) }} {{ sumAmountFromTransactions(bill.transactions) }}
                                </td>
                                <!-- Payment Status  -->
                                <td
                                    :class="getPaymentStatus(bill) === 'Paid' ? 'bg-green-400' : 'bg-yellow-400'"
                                    class="inline-block px-2 py-1 mt-1 text-sm text-white rounded"
                                >
                                    {{ hasPendingUserPayments(bill) ? 'Declared' : getPaymentStatus(bill) }}
                                </td>
                                <!-- Date  -->
                                <td>
                                    {{ bill.created_at | fromNow }}
                                </td>
                            </tr>

                            <!-- past title -->
                            <tr
                                v-if="props.data[4].length"
                                :class="props.trClass"
                                class="font-bold"
                            >
                                <td
                                    :class="props.tdClass"
                                    :colspan="currentTableHeaders.length"
                                >
                                    Past
                                </td>
                            </tr>
                            <!-- past bills -->
                            <tr
                                v-for="bill in props.data[4]"
                                :key="'bill-' + bill.id"
                                :class="[props.trClass]"
                                class="cursor-pointer"
                                @click="goto('retailer.invoice.print', { billId: bill.id })"
                            >
                                <!-- wholesaler name -->
                                <td
                                    v-if="!isWholesaler"
                                    :class="props.tdClass"
                                >
                                    {{ bill.wholesaler.name }}
                                </td>
                                <!-- Retailer Name -->
                                <td :class="props.tdClass">
                                    {{ bill.user.name }}
                                </td>
                                <!-- transaction count -->
                                <td :class="props.tdClass">
                                    {{ bill.transactions.length }}
                                </td>
                                <!-- total items in transaction -->
                                <td :class="props.tdClass">
                                    {{ getTotalQuantity(bill.transactions) }}
                                </td>
                                <!-- total transaction amount -->
                                <td :class="[props.tdClass, 'text-green-500']">
                                    {{ config(common.currency) }} {{ sumAmountFromTransactions(bill.transactions) }}
                                </td>
                                <!-- Payment Status  -->
                                <td
                                    :class="getPaymentStatus(bill) === 'Paid' ? 'bg-green-400' : 'bg-yellow-400'"
                                    class="inline-block px-2 py-1 mt-1 text-sm text-white rounded"
                                >
                                    {{ hasPendingUserPayments(bill) ? 'Declared' : getPaymentStatus(bill) }}
                                </td>
                                <!-- Date  -->
                                <td>
                                    {{ bill.created_at | fromNow }}
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </t-table>

                <div class="my-4">
                    <t-pagination
                        v-model="currentPage"
                        :total-items="totalRows"
                        :per-page="perPage"
                        :limit="limit"
                        :disabled="disabled"
                        @change="fetchPageData"
                    />
                </div>
            </div>

            <div v-else>No bills</div>
        </div>
    </AppLayout>
</template>

<script>
    import moment from 'moment';
    import _ from 'lodash';
    import AppLayout from '../../Layouts/AppLayout.vue';
    import common from '../../common';

    export default {
        name: '_RetailerReportIndex',

        components: {
            AppLayout,
        },

        props: {
            bills: {
                type: Object,
                required: true,
            },

            oldSearch: {
                type: String,
                default: null,
            },
        },

        data() {
            return {
                common,
                search: this.oldSearch,
                currentPage: null,
                limit: 5,
                perPage: this.bills.per_page,
                totalRows: this.bills.total,
                tableHeaders: [
                    'Wholesaler Name',
                    'Retailer Name',
                    'Items Sold',
                    'Total Item Count',
                    'Sales Amount',
                    'Payment Status',
                    'Date',
                ],
            };
        },

        computed: {
            disabled() {
                return !this.bills.data.length;
            },
            currentTableHeaders() {
                if (this.isWholesaler) {
                    return this.tableHeaders.filter((each) => each !== 'Wholesaler Name');
                }
                return this.tableHeaders;
            },
        },

        watch: {
            search: {
                handler() {
                    this.debounceFetchSearchResults();
                },
            },
        },

        mounted() {
            this.debounceFetchSearchResults = _.debounce(this.fetchSearchResults, 1000);
            document.getElementById('retailer-name-search-box').focus();
        },

        created() {
            this.currentPage = this.bills.current_page;
        },

        methods: {
            getPaymentStatus(bill) {
                return bill.payment_status;
            },

            hasPendingUserPayments(bill) {
                const pendingBills = bill.user_payments.filter(
                    (payment) => payment.accepted_at === null && payment.deleted_at === null,
                );
                return pendingBills.length;
            },

            getTotalQuantity(transactions) {
                let totalQty = 0;
                transactions.forEach((transaction) => {
                    const qty = parseFloat(transaction.qty);
                    totalQty += qty;
                });
                return totalQty;
            },

            sumAmountFromTransactions(transactions) {
                let sumGrandTotal = 0;
                transactions.forEach((transaction) => {
                    sumGrandTotal += parseFloat(transaction.grand_total);
                });
                return sumGrandTotal.toFixed(2);
            },

            groupedBills() {
                const groupedBills = [
                    [], // today
                    [], // yesterday
                    [], // thisWeek
                    [], // lastWeek
                    [], // past
                ];

                const today = moment();
                const yesterday = moment().subtract(1, 'day');

                this.bills.data.forEach((bill) => {
                    if (moment(bill.updated_at).isSame(today, 'day')) {
                        groupedBills[0].push(bill);
                    } else if (moment(bill.updated_at).isSame(yesterday, 'day')) {
                        groupedBills[1].push(bill);
                    } else if (moment(bill.updated_at).week() === moment().week()) {
                        groupedBills[2].push(bill);
                    } else if (moment(bill.updated_at).week() === moment().subtract(1, 'week').week()) {
                        groupedBills[3].push(bill);
                    } else {
                        groupedBills[4].push(bill);
                    }
                });

                return groupedBills;
            },

            fetchPageData(newPage) {
                this.$inertia.get(
                    route('reports.index', {
                        page: newPage,
                        search: this.search,
                    }),
                );
            },

            fetchSearchResults() {
                this.$inertia.get(
                    route('reports.index', {
                        page: this.currentPage,
                        search: this.search,
                    }),
                );
            },
        },
    };
</script>
